/**
 * The board's filter surface: a rail of columns, each listing its own values,
 * replacing the "Filter by column" dropdown that could only add a column and
 * never show what was inside it.
 *
 * Every value is one click away, and each row carries the number of items
 * behind it, counted over the UNNARROWED board — the row answers "how many
 * items have this value", not "how many would survive if you picked it".
 *
 * The footer switches to advanced mode, where the same columns are expressed as
 * ordered `is` / `is not` rules. The two modes compose (both narrow the board at
 * once) but only one is on screen, and "Clear all" empties both. A board that
 * narrows on the server leaves the rules out entirely and the footer with them,
 * rather than offering an `is not` its endpoint cannot express.
 */
import { Button, Popover, PopoverContent, PopoverTrigger } from '@monksflow/monks-ui';
import { ListFilterIcon, PlusIcon } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { CountBadge } from '@/shared/ui';
import {
  newFilterRule,
  type FilterColumnSpec,
  type FilterRule,
  type FilterValueCounts,
  type FilterValueOption,
} from '../../lib/filtering';
import type { BoardFilters } from '../../lib/usePersistentFilters';
import { FilterRuleRow, ValueDot } from '../FilterRuleRow';

/** How many remembered columns the Recent filters section shows. */
const RECENT_SHOWN = 2;

const EMPTY_RULES: readonly FilterRule[] = [];

export interface TrackersFilterPanelProps {
  columns: ReadonlyArray<FilterColumnSpec>;
  filters: BoardFilters;
  onFiltersChange: (next: BoardFilters) => void;
  /**
   * The advanced screen's `is` / `is not` rules. Both are omitted together by a
   * board whose narrowing happens server-side: the rows endpoint has no
   * negation, so an `is not` there would be a condition that quietly matched
   * everything. Without them the advanced screen is unreachable.
   */
  rules?: readonly FilterRule[];
  onRulesChange?: (next: readonly FilterRule[]) => void;
  /**
   * How many selections the board is actually applying. It comes from the board
   * rather than being recounted here because the two can differ: a people
   * column is matched on before the directory can name it, and a rule whose
   * column was deleted narrows nothing at all.
   */
  activeFilterCount: number;
  counts: FilterValueCounts;
  /**
   * Rows the board is showing now, against its unnarrowed total. Omitted by a
   * board that filters server-side, which is never told how many rows it would
   * have had; the summary then says nothing rather than reporting the narrowed
   * count as the whole.
   */
  visibleItems?: number;
  totalItems?: number;
  /** Most recently filtered columns, most recent first. */
  recentColumnIds: readonly string[];
  onColumnUsed: (columnId: string) => void;
}

export function TrackersFilterPanel({
  columns,
  filters,
  onFiltersChange,
  rules,
  onRulesChange,
  activeFilterCount,
  counts,
  visibleItems,
  totalItems,
  recentColumnIds,
  onColumnUsed,
}: TrackersFilterPanelProps) {
  const { t } = useTranslation('trackers');

  const hasRulesMode = Boolean(onRulesChange);
  const activeRules = rules ?? EMPTY_RULES;

  // Opens on whichever mode is already narrowing the board, so restored rules
  // are never applied from a screen that does not show them.
  const [advanced, setAdvanced] = useState(() => activeRules.length > 0);

  const toggleValue = (columnId: string, value: string) => {
    const current = filters[columnId] ?? [];
    const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
    // Rebuild without the key rather than storing an empty array, so a column
    // the user emptied leaves no trace in the persisted filters.
    onFiltersChange(
      next.length > 0
        ? { ...filters, [columnId]: next }
        : Object.fromEntries(Object.entries(filters).filter(([key]) => key !== columnId)),
    );
    onColumnUsed(columnId);
  };

  const recentColumns = recentColumnIds
    .flatMap((id) => columns.filter((c) => c.id === id))
    .slice(0, RECENT_SHOWN);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="default"
          // 12px gutters, so this lines up with the Sort and Group by chips
          // beside it rather than sitting on the wider default 16px.
          className="px-3"
          /* The count renders inside the button, but an aria-label
             short-circuits name-from-content, so it has to be spelled out
             here or a screen reader never hears it. */
          aria-label={
            activeFilterCount > 0
              ? t('trackersBoard.trackersFilterPanel.openFiltersWithCount', {
                  count: activeFilterCount,
                })
              : t('trackersBoard.trackersFilterPanel.openFilters')
          }
        >
          <ListFilterIcon className="text-muted-foreground" aria-hidden="true" />
          {t('trackersBoard.trackersToolbar.filter')}
          <CountBadge count={activeFilterCount} />
        </Button>
      </PopoverTrigger>
      {/* 720px, but never wider than the viewport allows; `collisionPadding`
          keeps the same 16px gutter the design clamps to by hand. */}
      <PopoverContent
        align="start"
        collisionPadding={16}
        className="flex max-h-[32rem] w-180 max-w-[calc(100vw-2rem)] flex-col p-0"
      >
        <div className="flex items-center gap-3 border-b border-border px-4 py-3">
          <h2 className="text-sm font-medium text-foreground">
            {advanced
              ? t('trackersBoard.trackersFilterPanel.advancedFilters')
              : t('trackersBoard.trackersFilterPanel.quickFilters')}
          </h2>
          {totalItems === undefined ? null : (
            <span className="text-xs text-muted-foreground">
              {activeFilterCount > 0
                ? t('trackersBoard.trackersFilterPanel.showingSome', {
                    visible: visibleItems ?? totalItems,
                    count: totalItems,
                  })
                : t('trackersBoard.trackersFilterPanel.showingAll', { count: totalItems })}
            </span>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="ml-auto"
            disabled={activeFilterCount === 0}
            onClick={() => {
              // Both modes at once: the summary counts them together, so
              // clearing one and leaving the other would contradict it.
              onFiltersChange({});
              onRulesChange?.([]);
            }}
          >
            {t('trackersBoard.trackersFilterPanel.clearAll')}
          </Button>
        </div>

        {columns.length === 0 ? (
          <p className="p-4 text-sm text-muted-foreground">
            {t('trackersBoard.trackersToolbar.noFilterableColumns')}
          </p>
        ) : advanced && onRulesChange ? (
          <div className="min-h-0 flex-1 overflow-y-auto px-4 pt-1 pb-5">
            <AdvancedFilters columns={columns} rules={activeRules} onRulesChange={onRulesChange} />
          </div>
        ) : (
          // One rail rather than a stack: every column keeps its own vertical
          // list, and it is the rail that scrolls sideways once the columns
          // outrun 720px. Wrapping them instead would put the last column's
          // values above the first column's heading.
          <div className="flex min-h-0 flex-1 items-stretch overflow-x-auto px-4 pb-5">
            {recentColumns.length > 0 && (
              <FilterSection
                title={t('trackersBoard.trackersFilterPanel.recentFilters')}
                className="border-r border-border-light pr-4"
              >
                {recentColumns.map((column) => (
                  <FilterColumnBlock
                    key={column.id}
                    column={column}
                    selected={filters[column.id]}
                    counts={counts.get(column.id)}
                    onToggle={toggleValue}
                  />
                ))}
              </FilterSection>
            )}
            <FilterSection
              title={t('trackersBoard.trackersFilterPanel.allColumns')}
              className={recentColumns.length > 0 ? 'pl-4' : ''}
            >
              {columns.map((column) => (
                <FilterColumnBlock
                  key={column.id}
                  column={column}
                  selected={filters[column.id]}
                  counts={counts.get(column.id)}
                  onToggle={toggleValue}
                />
              ))}
            </FilterSection>
          </div>
        )}

        {hasRulesMode ? (
          <div className="flex justify-end border-t border-border bg-card px-4 py-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 text-muted-foreground"
              onClick={() => {
                setAdvanced((prev) => !prev);
              }}
            >
              {advanced
                ? t('trackersBoard.trackersFilterPanel.switchToQuick')
                : t('trackersBoard.trackersFilterPanel.switchToAdvanced')}
            </Button>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}

function AdvancedFilters({
  columns,
  rules,
  onRulesChange,
}: {
  columns: ReadonlyArray<FilterColumnSpec>;
  rules: readonly FilterRule[];
  onRulesChange: (next: readonly FilterRule[]) => void;
}) {
  const { t } = useTranslation('trackers');

  const replaceRule = (id: string, patch: Partial<FilterRule>) => {
    onRulesChange(rules.map((rule) => (rule.id === id ? { ...rule, ...patch } : rule)));
  };

  return (
    // Indented past the `Where` column, so the empty line and the add button
    // both start where the conditions themselves do.
    <div className="flex flex-col items-start gap-3">
      {rules.length === 0 ? (
        <p className="pl-13 text-sm text-muted-foreground">
          {t('trackersBoard.trackersFilterPanel.noConditions')}
        </p>
      ) : (
        rules.map((rule, index) => (
          <FilterRuleRow
            key={rule.id}
            rule={rule}
            columns={columns}
            label={
              index === 0
                ? t('trackersBoard.trackersFilterPanel.where')
                : t('trackersBoard.trackersFilterPanel.and')
            }
            onChange={(patch) => {
              replaceRule(rule.id, patch);
            }}
            onRemove={() => {
              onRulesChange(rules.filter((r) => r.id !== rule.id));
            }}
          />
        ))
      )}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="ml-13"
        onClick={() => {
          onRulesChange([...rules, newFilterRule(columns[0].id)]);
        }}
      >
        <PlusIcon aria-hidden="true" />
        {t('trackersBoard.trackersFilterPanel.newFilter')}
      </Button>
    </div>
  );
}

function FilterSection({
  title,
  className,
  children,
}: {
  title: string;
  className?: string;
  children: ReactNode;
}) {
  const headingId = useId();

  // Named, so the two sections are distinguishable landmarks rather than one
  // undifferentiated wall of chips.
  return (
    <section
      aria-labelledby={headingId}
      className={`flex shrink-0 flex-col gap-4 ${className ?? ''}`}
    >
      <h3 id={headingId} className="type-label-md text-foreground">
        {title}
      </h3>
      <div className="flex items-start gap-3">{children}</div>
    </section>
  );
}

function FilterColumnBlock({
  column,
  selected,
  counts,
  onToggle,
}: {
  column: FilterColumnSpec;
  selected: readonly string[] | undefined;
  counts: ReadonlyMap<string, number> | undefined;
  onToggle: (columnId: string, value: string) => void;
}) {
  const { t } = useTranslation('trackers');
  const labelId = useId();

  // Value labels repeat across columns (every date column offers "Overdue"),
  // so the column name is what tells two chips apart.
  return (
    <div role="group" aria-labelledby={labelId} className="flex w-45 shrink-0 flex-col gap-1.5">
      <div className="type-body-sm flex min-w-0 items-baseline gap-1 text-muted-foreground">
        <span id={labelId} className="truncate">
          {column.label}
        </span>
        <span className="shrink-0">
          {t('trackersBoard.trackersFilterPanel.valueCount', { count: column.options.length })}
        </span>
      </div>
      {/* Each column scrolls on its own, so a long list lengthens nothing but
          itself and the rail keeps every heading on one line. */}
      <div className="flex max-h-56 flex-col gap-1 overflow-y-auto">
        {column.options.map((option) => (
          <FilterValueRow
            key={option.value}
            option={option}
            count={counts?.get(option.value) ?? 0}
            selected={selected?.includes(option.value) ?? false}
            onToggle={() => {
              onToggle(column.id, option.value);
            }}
          />
        ))}
      </div>
    </div>
  );
}

function FilterValueRow({
  option,
  count,
  selected,
  onToggle,
}: {
  option: FilterValueOption;
  count: number;
  selected: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation('trackers');

  return (
    <button
      type="button"
      aria-pressed={selected}
      /* Name-from-content would read the bare number after the label; spelling
         it out is the only way the count reaches a screen reader as a count. */
      aria-label={t('trackersBoard.trackersFilterPanel.valueWithCount', {
        label: option.label,
        count,
      })}
      onClick={onToggle}
      // Weight, not colour, carries the selection: a column of rows that dim
      // when unpicked reads as half of them being disabled.
      className={`flex h-[34px] w-full shrink-0 cursor-pointer items-center gap-2 rounded-md border px-2.5 text-left text-sm text-foreground transition-colors duration-fast ease-out hover:bg-secondary focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none ${
        selected ? 'border-border-strong bg-accent font-medium' : 'border-border-light font-normal'
      }`}
    >
      <ValueDot color={option.color} />
      <span className="min-w-0 flex-1 truncate">{option.label}</span>
      <span className="type-body-sm shrink-0 tabular-nums text-muted-foreground">{count}</span>
    </button>
  );
}
